import {Directive, ElementRef, HostBinding, Inject, OnDestroy, OnInit, Optional, Renderer2, RendererStyleFlags2} from '@angular/core';
import {Block, BlockType} from '@paperlessio/sdk/api/models';
import {BlockComponent} from '@blocks/block/block.component';
import {DomSanitizer} from '@angular/platform-browser';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {ParticipationFlowStore} from '@shared/participants/participation-flow.store';
import {Subscription} from 'rxjs';
import {PdfPartComponent} from '@blocks/pdf-part/pdf-part.component';
import {SubmissionStore} from '@paperlessio/sdk/api/stores';

/**
 * Directive to be used on block components. It sets the css color variables according to the assigned block owner
 * participant ids.
 */
@Directive({selector: '[blockOwnerColorization]'})
export class BlockOwnerColorizationDirective implements OnInit, OnDestroy {
  private ownerCache: number[];
  private subs = new Subscription();

  FALLBACK_BG_COLOR = '#eee';
  FALLBACK_BORDER_COLOR = '#666';

  @HostBinding('class.participant-coloring') private enabled = false;
  @HostBinding('class.participant-coloring-multiple') private multiple = false;

  constructor(private renderer: Renderer2,
              @Inject(BLOCKS_CONFIG) public blocksConfig: BlocksConfig,
              private blockComponent: BlockComponent,
              private sanitizer: DomSanitizer,
              private el: ElementRef,
              @Optional() public pdfPartComponent: PdfPartComponent,
              @Optional() private submissionStore: SubmissionStore,
              @Optional() private participantFlowStore: ParticipationFlowStore) {
  }

  get isOverlay(): boolean {
    return !!this.pdfPartComponent;
  }

  ngOnInit() {
    this.enabled = this.blocksConfig.participantColoring && this.blockComponent.block.isInput;
    if (this.enabled) {
      this.setParticipantColoringStyle(this.blockComponent.block); // updates are triggered from block component
      this.subs.add(this.blockComponent.blockChanged.subscribe(block => {
        this.setParticipantColoringStyle(block);
      }));
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private setParticipantColoringStyle(block: Block) {
    if (!block) {
      return;
    }

    // Combine the block owners from the bucket/submittable with the block owners of the submission (if available)
    const block_owner_participant_ids = [...block.block_owner_participant_ids];
    const block_owner_participant_ids_from_submission = this.submissionStore?.blockOwnerParticipantIdsForBlock(block.id);
    if (block_owner_participant_ids_from_submission?.length) {
      block_owner_participant_ids.push(...block_owner_participant_ids_from_submission);
    }

    // @ts-ignore
    // tslint:disable-next-line:triple-equals
    if (block_owner_participant_ids == '' + this.ownerCache) {
      return;
    } else {
      this.ownerCache = [...block_owner_participant_ids];
    }

    const owners = block_owner_participant_ids
      // filter out participants which are not loaded in the ParticipantFlowStore, i.e. the ones from the bucket when displaying a
      // submission and vice versa
      .map(id => this.participantFlowStore.getParticipant(id))
      .filter(participant => !!participant?.color);

    // dedupe
    owners.filter((value, index) => owners.indexOf(value) === index);

    this.multiple = owners.length > 1;

    const participant = owners[0];
    let bgColor: string;
    if (block.parent.type === BlockType.PdfPart) {
      bgColor = participant?.backgroundColorLightLowAlpha || this.FALLBACK_BG_COLOR;
    } else {
      bgColor = this.isOverlay ? participant?.backgroundColorHighAlpha : participant?.backgroundColor || this.FALLBACK_BG_COLOR;
    }

    this.renderer.setStyle(
      this.el.nativeElement,
      '--participant-background-color',
      bgColor,
      RendererStyleFlags2.DashCase
    );
    this.renderer.setStyle(
      this.el.nativeElement,
      '--participant-border-color',
      participant?.borderColor || this.FALLBACK_BORDER_COLOR,
      RendererStyleFlags2.DashCase
    );
    this.renderer.setStyle(this.el.nativeElement,
      '--participant-color',
      participant?.color || this.FALLBACK_BORDER_COLOR,
      RendererStyleFlags2.DashCase
    );

    if (this.multiple) {
      // rotation of the segments
      const deg = 45;
      // assumes 42px high text-inputs plus 3px because of border-bottom, border-top and the last white 1px divider
      const size = 45 / owners.length * Math.sin((90-deg) * (Math.PI/180));
      let bgGradient = `repeating-linear-gradient( -${deg}deg, `;
      bgGradient += owners.map((participant, i) => {
        return `${participant.color} ${i*size}px, ${participant.color} ${((i+1)*size)-1}px, #fff ${((i+1)*size)-1}px, #fff ${(i+1)*size}px`;
      }).join(', ');
      bgGradient += ')';
      this.renderer.setStyle(this.el.nativeElement, '--participants-background-gradient', bgGradient, RendererStyleFlags2.DashCase);
    }
  }
}
